/*
 * SplashPad PWA runtime helpers.
 *
 * Provides:
 *   • registerServiceWorker()          — safe SW registration (prod / preview only).
 *   • isStandalone()                   — true when the page is running inside
 *                                        an installed PWA (Chrome/Edge/iOS Safari).
 *   • isIosSafari()                    — true for iPhone/iPad Safari, where
 *                                        `beforeinstallprompt` doesn't fire and
 *                                        we must show manual A2HS instructions.
 *   • usePwaInstall()                  — React hook returning
 *                                          { installable, isIOS, installed,
 *                                            promptInstall, iosGuideOpen,
 *                                            openIosGuide, closeIosGuide,
 *                                            dismissedThisSession, dismissForSession }
 *   • applyCoachManifestBranding()     — swaps <link rel="manifest"> to a
 *                                        blob URL carrying the coach's business
 *                                        name + `logo_url` so the installed icon
 *                                        + splash screen match their brand.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// ---- Detection helpers ------------------------------------------------------

export function isStandalone() {
    if (typeof window === 'undefined') return false;
    // Chrome / Edge / Firefox / Android
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    // iOS Safari (older, still current for many devices)
    if (window.navigator && window.navigator.standalone === true) return true;
    return false;
}

export function isIosSafari() {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent || '';
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (!isIos) return false;
    // Exclude Chrome/Firefox/Edge on iOS which report their own vendor.
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    return isSafari;
}

// ---- Service worker registration -------------------------------------------

export function registerServiceWorker() {
    if (typeof window === 'undefined') return;
    if (!('serviceWorker' in navigator)) return;
    // Dev mode uses hot-reload; SW would fight the CRA dev server. Only
    // register in production / preview builds.
    const host = window.location.hostname;
    const isLocalDev = host === 'localhost' || host === '127.0.0.1';
    if (isLocalDev) return;

    window.addEventListener('load', () => {
        navigator.serviceWorker
            .register('/service-worker.js', { scope: '/' })
            .catch((err) => {
                // Non-fatal — the app still works fully online without a SW.
                console.warn('[pwa] SW registration failed:', err);
            });
    });
}

// ---- beforeinstallprompt harness -------------------------------------------
// The event fires ONCE, and we must call `.preventDefault()` synchronously to
// keep the ability to trigger it later. We stash it on window so multiple
// components (banner + sidebar link) share the same handle.

const DEFERRED_KEY = '__splashpadDeferredInstallPrompt';

function getDeferred() {
    if (typeof window === 'undefined') return null;
    return window[DEFERRED_KEY] || null;
}

function setDeferred(evt) {
    if (typeof window === 'undefined') return;
    window[DEFERRED_KEY] = evt;
    window.dispatchEvent(new CustomEvent('splashpad:pwa-installable'));
}

function clearDeferred() {
    if (typeof window === 'undefined') return;
    window[DEFERRED_KEY] = null;
    // A consumed or dismissed prompt is not a completed installation.
    window.dispatchEvent(new CustomEvent('splashpad:pwa-installable'));
}

// Bind global listeners once per page load.
if (typeof window !== 'undefined' && !window.__splashpadPwaBound) {
    window.__splashpadPwaBound = true;
    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        setDeferred(e);
    });
    window.addEventListener('appinstalled', () => {
        clearDeferred();
        window.__splashpadPwaInstalled = true;
        window.dispatchEvent(new CustomEvent('splashpad:pwa-installed'));
    });
}

// ---- Session-dismiss helpers -----------------------------------------------

const DISMISS_KEY = 'splashpad_pwa_install_dismissed';

function isDismissedThisSession() {
    try {
        return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch (_e) {
        return false;
    }
}

function dismissForSessionInternal() {
    try {
        sessionStorage.setItem(DISMISS_KEY, '1');
    } catch (_e) {
        // ignore quota / privacy errors
    }
}

// ---- React hook -------------------------------------------------------------

export function usePwaInstall() {
    const [installed, setInstalled] = useState(() => isStandalone() || Boolean(typeof window !== 'undefined' && window.__splashpadPwaInstalled));
    const [installable, setInstallable] = useState(!!getDeferred());
    const [iosGuideOpen, setIosGuideOpen] = useState(false);
    const [dismissedThisSession, setDismissed] = useState(isDismissedThisSession());
    const iosRef = useRef(isIosSafari());

    useEffect(() => {
        const onInstallable = () => setInstallable(Boolean(getDeferred()));
        const onInstalled = () => {
            setInstalled(true);
            setInstallable(false);
        };
        const onDisplayChange = (e) => {
            if (e.matches) setInstalled(true);
        };
        window.addEventListener('splashpad:pwa-installable', onInstallable);
        window.addEventListener('splashpad:pwa-installed', onInstalled);
        const mm = window.matchMedia('(display-mode: standalone)');
        if (mm.addEventListener) mm.addEventListener('change', onDisplayChange);
        else if (mm.addListener) mm.addListener(onDisplayChange);

        return () => {
            window.removeEventListener('splashpad:pwa-installable', onInstallable);
            window.removeEventListener('splashpad:pwa-installed', onInstalled);
            if (mm.removeEventListener) mm.removeEventListener('change', onDisplayChange);
            else if (mm.removeListener) mm.removeListener(onDisplayChange);
        };
    }, []);

    const promptInstall = useCallback(async () => {
        if (iosRef.current) {
            setIosGuideOpen(true);
            return 'ios-guide';
        }
        const evt = getDeferred();
        if (!evt) return 'unavailable';
        try {
            await evt.prompt();
            const choice = await evt.userChoice;
            clearDeferred();
            if (choice?.outcome === 'accepted') {
                // Only appinstalled or standalone mode confirms completion.
                return 'accepted';
            }
            return 'dismissed';
        } catch (_err) {
            clearDeferred();
            return 'error';
        }
    }, []);

    const openIosGuide = useCallback(() => setIosGuideOpen(true), []);
    const closeIosGuide = useCallback(() => setIosGuideOpen(false), []);
    const dismissForSession = useCallback(() => {
        dismissForSessionInternal();
        setDismissed(true);
    }, []);

    return {
        installed,
        installable,
        isIOS: iosRef.current,
        iosGuideOpen,
        openIosGuide,
        closeIosGuide,
        promptInstall,
        dismissedThisSession,
        dismissForSession,
    };
}

// ---- Dynamic per-coach manifest --------------------------------------------
// Phase 75 built the coach manifest in the browser as a blob: URL. Phase 179
// moved it server-side: Chrome stores the manifest URL at install time and
// re-fetches it on launch to detect icon changes — a blob URL no longer
// exists after the page unloads, so a changed logo never reached the
// installed app. Now <link rel="manifest"> points at a STABLE https URL
// (/api/pwa/coach/{id}/manifest.webmanifest) whose icons are real square
// PNGs rendered from the logo, versioned by a hash of the logo URL.

let _lastCoachManifestBlob = null;
let _appliedBrandingKey = null;

function isSafeUrl(candidate) {
    if (!candidate) return false;
    try {
        const u = new URL(candidate, window.location.origin);
        return u.protocol === 'https:' || u.protocol === 'http:' || u.protocol === 'data:' || u.origin === window.location.origin;
    } catch (_e) {
        return false;
    }
}

// Mirrors backend `logo_version()` closely enough for cache-busting: any
// change in the logo URL yields a different token.
function logoVersion(logoUrl) {
    let h = 5381;
    const s = String(logoUrl || '');
    for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0; // djb2
    return h.toString(36);
}

function apiBase() {
    // Same-origin `/api` in production/preview (ingress proxies it); the env
    // host only differs during local development.
    try {
        const envBase = process.env.REACT_APP_BACKEND_URL || '';
        const here = window.location;
        const isLocalDev = here.hostname === 'localhost' || here.hostname === '127.0.0.1';
        if (isLocalDev && envBase) return `${envBase}/api`;
        return `${here.origin}/api`;
    } catch (_e) {
        return '/api';
    }
}

function setLinkHref(selector, href, create) {
    let el = document.querySelector(selector);
    if (!el && create) {
        el = document.createElement('link');
        for (const [k, v] of Object.entries(create)) el.setAttribute(k, v);
        document.head.appendChild(el);
    }
    if (el && el.getAttribute('href') !== href) el.setAttribute('href', href);
    return el;
}

/** Put the <head> back to the stock SplashPad manifest + icons. */
export function resetManifestBranding() {
    if (typeof document === 'undefined') return;
    setLinkHref('link[rel="manifest"]', '/manifest.json', { rel: 'manifest' });
    document.querySelectorAll('link[rel="apple-touch-icon"]').forEach((el) => {
        const sizes = el.getAttribute('sizes');
        el.setAttribute('href', sizes === '512x512' ? '/pwa-icon-512.png' : sizes === '192x192' ? '/pwa-icon-192.png' : '/favicon.png');
    });
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) themeMeta.setAttribute('content', '#0a1628');
    if (_lastCoachManifestBlob) {
        try { URL.revokeObjectURL(_lastCoachManifestBlob); } catch (_e) { /* ignore */ }
        _lastCoachManifestBlob = null;
    }
    _appliedBrandingKey = null;
}

/**
 * Point the app manifest + apple-touch-icons at the coach-branded, server-
 * generated versions. Safe to call on every render — no-ops when nothing
 * changed, resets to stock when the coach has no logo.
 *
 * @param {object} opts
 * @param {string} opts.coachId      — instructor_profile id (required for branding).
 * @param {string} opts.displayName  — coach business name / display name (unused in
 *                                     the manifest name — owner decision — kept for callers).
 * @param {string} opts.logoUrl      — coach's uploaded logo (https / data URL).
 * @param {string} [opts.themeColor] — brand primary (falls back to SplashPad navy).
 */
export function applyCoachManifestBranding({ coachId, logoUrl, themeColor } = {}) {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!coachId || !logoUrl || !isSafeUrl(logoUrl)) {
        // Coach removed their logo (or none yet) → stock SplashPad app.
        if (_appliedBrandingKey) resetManifestBranding();
        return;
    }

    const version = logoVersion(logoUrl);
    const color = /^#[0-9a-f]{6}$/i.test(themeColor || '') ? themeColor : '#0a1628';
    const key = `${coachId}|${version}|${color}`;
    if (key === _appliedBrandingKey) return;

    try {
        const base = apiBase();
        const manifestUrl = `${base}/pwa/coach/${encodeURIComponent(coachId)}/manifest.webmanifest`;
        const icon = (spec) => `${base}/pwa/coach/${encodeURIComponent(coachId)}/icon/${spec}?v=${version}`;

        // Manifest — stable URL (no version param) so the browser's install
        // record keeps pointing at something it can re-check; the response
        // itself is served `no-cache`.
        setLinkHref('link[rel="manifest"]', manifestUrl, { rel: 'manifest' });
        if (_lastCoachManifestBlob) {
            try { URL.revokeObjectURL(_lastCoachManifestBlob); } catch (_e) { /* ignore */ }
            _lastCoachManifestBlob = null;
        }

        // apple-touch-icon — iOS Safari uses THESE (not the manifest) for the
        // home-screen icon and prefers the entry whose `sizes` is closest to
        // 180x180. index.html ships three; update every one of them (Phase 75
        // only touched the first, so iOS kept picking the SplashPad 192 icon).
        const appleLinks = document.querySelectorAll('link[rel="apple-touch-icon"]');
        if (appleLinks.length === 0) {
            setLinkHref('link[rel="apple-touch-icon"]', icon('180.png'), { rel: 'apple-touch-icon', sizes: '180x180' });
        } else {
            appleLinks.forEach((el) => {
                const sizes = el.getAttribute('sizes');
                const spec = sizes === '512x512' ? '512.png' : sizes === '192x192' ? '192.png' : '180.png';
                if (!sizes) el.setAttribute('sizes', '180x180');
                el.setAttribute('href', icon(spec));
            });
        }

        // theme-color (address bar tint on Android Chrome).
        let themeMeta = document.querySelector('meta[name="theme-color"]');
        if (!themeMeta) {
            themeMeta = document.createElement('meta');
            themeMeta.setAttribute('name', 'theme-color');
            document.head.appendChild(themeMeta);
        }
        themeMeta.setAttribute('content', color);

        _appliedBrandingKey = key;
    } catch (_err) {
        // ignore — falls back to the static manifest.
    }
}

/**
 * Human-readable note about how/when the installed app icon follows a logo
 * change. Shared by Settings (after saving a new logo) and the install guide.
 */
export const APP_ICON_UPDATE_NOTE = {
    android: 'Android and desktop pick up your new logo on their own — usually the next time the app is opened (it can take up to a day).',
    ios: 'iPhone and iPad can’t refresh an installed icon. Remove the SplashPad app from your home screen, then add it again from Safari (Share → Add to Home Screen) to see the new logo.',
};
